import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useEffect } from 'react';
import HomePage from './pages/HomePage';
import ChurchesPage from './pages/ChurchesPage';
import NewsPage from './pages/NewsPage';
import EventsPage from './pages/EventsPage';
import LiturgiPage from './pages/LiturgiPage';
import PrastensHornaPage from './pages/PrastensHornaPage';
import AlbumsListPage from './pages/forsamling/AlbumsListPage';
import AlbumDetailPage from './pages/forsamling/AlbumDetailPage';
import BibleReadingsPage from './pages/BibleReadingsPage';
import FordjupningPage from './pages/FordjupningPage';
import KristnaLaranPage from './pages/KristnaLaranPage';
import StiftelserPage from './pages/StiftelserPage';
import MassesPage from './pages/MassesPage';
import ConvertPage from './pages/ConvertPage';
import TrosundervisningPage from './pages/TrosundervisningPage';
import UngdomarPage from './pages/UngdomarPage';
import ContactPage from './pages/ContactPage';
import CaritasPage from './pages/forsamling/CaritasPage';
import CataloguePage from './pages/forsamling/CataloguePage';
import MedlemskapPage from './pages/ovrigt/MedlemskapPage';
import KallelsePage from './pages/ovrigt/KallelsePage';
import BegravningPage from './pages/ovrigt/BegravningPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import FloatingLanguageSelector from './components/FloatingLanguageSelector';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { EventsModuleProvider } from './context/EventsModuleContext';
import './App.css';

// Supported language codes
const SUPPORTED_LANGUAGES = ['sv', 'en', 'pl', 'es', 'ar', 'fr', 'de', 'it', 'pt', 'hr', 'vi', 'tl', 'ko', 'zh', 'ta', 'si', 'ti', 'am'];

// Component to handle language from URL
const LanguageRouter = ({ children }) => {
  const { lang } = useParams();
  const { changeLanguage, language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // If there's a language param in URL, update the context
    if (lang && SUPPORTED_LANGUAGES.includes(lang) && lang !== language) {
      changeLanguage(lang);
    }
  }, [lang, changeLanguage, language]);

  return children;
};

// Wrapper component that extracts language and renders the page
const LocalizedPage = ({ Component }) => {
  return (
    <LanguageRouter>
      <Component />
    </LanguageRouter>
  );
};

// Define all routes once
const routes = [
  { path: '', element: HomePage },
  { path: 'churches', element: ChurchesPage },
  { path: 'news', element: NewsPage },
  { path: 'events', element: EventsPage },
  { path: 'liturgi', element: LiturgiPage },
  { path: 'prastens-horna', element: PrastensHornaPage },
  { path: 'forsamling/album', element: AlbumsListPage },
  { path: 'forsamling/album/:id', element: AlbumDetailPage },
  { path: 'bible', element: BibleReadingsPage },
  { path: 'fordjupning', element: FordjupningPage },
  { path: 'fordjupning/kristna-laran', element: KristnaLaranPage },
  { path: 'fordjupning/stiftelser', element: StiftelserPage },
  { path: 'fordjupning/masses', element: MassesPage },
  { path: 'fordjupning/convert', element: ConvertPage },
  { path: 'forsamling/trosundervisning', element: TrosundervisningPage },
  { path: 'forsamling/ungdomar', element: UngdomarPage },
  { path: 'forsamling/caritas', element: CaritasPage },
  { path: 'forsamling/katalog', element: CataloguePage },
  { path: 'ovrigt/medlemskap', element: MedlemskapPage },
  { path: 'ovrigt/begravning', element: BegravningPage },
  { path: 'ovrigt/kallelse', element: KallelsePage },
  { path: 'contact', element: ContactPage },
];

function AppRoutes() {
  return (
    <Routes>
      {/* Admin routes (no language prefix) */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/*" element={<AdminDashboard />} />
      
      {/* Default routes without language prefix (Swedish default) */}
      {routes.map(({ path, element: Element }) => (
        <Route 
          key={path || 'home'} 
          path={path ? `/${path}` : '/'} 
          element={<Element />} 
        />
      ))}
      
      {/* Language-prefixed routes */}
      {SUPPORTED_LANGUAGES.map(lang => (
        routes.map(({ path, element: Element }) => (
          <Route 
            key={`${lang}-${path || 'home'}`} 
            path={`/${lang}${path ? `/${path}` : ''}`} 
            element={<LocalizedPage Component={Element} />} 
          />
        ))
      )).flat()}
      
      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <ThemeProvider>
          <EventsModuleProvider>
            <BrowserRouter>
              <Toaster position="top-right" />
              <FloatingLanguageSelector />
              <AppRoutes />
            </BrowserRouter>
          </EventsModuleProvider>
        </ThemeProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
